// Shared SSR utilities for retailer integrations
// - Safe cookie/localStorage helpers
// - Session id management
// - SSR headers builder (language, supplier token, auth/authless, session)
// - Fetch SSR HTML with environment fallback
// - DOM ready + element wait/retry helpers
// - Optional v10 SDK configuration helper

import { env } from "../../environments/environment.js";
import { loadModuleScriptsFromHtml } from "./ssr-scripts.js";

/************************************** SESSION ID UTIL **************************************/

const SESSION_STORAGE_KEY = "_mealz/injector/session-id";

/**
 * Return the injector's session id, generating and persisting a uuid on first use.
 * @returns {string} Session ID
 */
export function getOrCreateSessionId() {
  let sessionId = sessionStorage.getItem(SESSION_STORAGE_KEY);
  if (sessionId) return sessionId;

  // crypto.randomUUID is only exposed in secure contexts; fall back on http/old browsers.
  const fallbackSessionId = `sid-${Date.now()}-${Math.random().toString(16).slice(2)}`;
  sessionId = crypto?.randomUUID?.() ?? fallbackSessionId;
  try {
    sessionStorage.setItem(SESSION_STORAGE_KEY, sessionId);
  } catch (_) {
    console.debug("[Mealz] sessionStorage setItem failed for sessionId");
  }
  return sessionId;
}

/************************************** AUTHLESS ID UTIL **************************************/

const AUTHLESS_STORAGE_KEY = "_mealz/injector/authless-id";

let pendingAuthlessFetch = null;

function clearStoredAuthlessId() {
  try {
    sessionStorage.removeItem(AUTHLESS_STORAGE_KEY);
  } catch (_) {
    console.debug("[Mealz] sessionStorage removeItem failed for authless-id");
  }
}

async function fetchAuthlessToken(supplierToken, baseUrl) {
  const resolvedBaseUrl = baseUrl ?? env?.ssr?.baseUrl;
  if (!resolvedBaseUrl) {
    throw new Error("[Mealz] baseUrl not configured");
  }

  const url = `${resolvedBaseUrl}/v3/generate-authless-token`;
  const response = await fetch(url, {
    method: "GET",
    headers: { "supplier-token": supplierToken },
  });
  if (!response.ok) {
    throw new Error(`[Mealz] ${url} fetch failed: ${response.status} ${response.statusText}`);
  }

  const payload = await response.json();
  const authlessId = payload?.authless_id;
  if (!authlessId) {
    throw new Error("[Mealz] generate-authless-token response missing authless_id");
  }
  return authlessId;
}

async function getOrCreateAuthlessId(supplierToken, baseUrl) {
  const storedAuthlessId = sessionStorage.getItem(AUTHLESS_STORAGE_KEY);
  if (storedAuthlessId) {
    return storedAuthlessId;
  }

  if (pendingAuthlessFetch) {
    return pendingAuthlessFetch;
  }

  pendingAuthlessFetch = fetchAuthlessToken(supplierToken, baseUrl)
    .then((authlessId) => {
      try {
        sessionStorage.setItem(AUTHLESS_STORAGE_KEY, authlessId);
      } catch (_) {
        console.debug("[Mealz] sessionStorage setItem failed for authless-id");
      }
      return authlessId;
    })
    .finally(() => {
      pendingAuthlessFetch = null;
    });

  return pendingAuthlessFetch;
}

export function getStoredAuthlessId() {
  return sessionStorage.getItem(AUTHLESS_STORAGE_KEY);
}

/************************************** SSR HTML FETCH UTILS **************************************/
/**
 * Build headers for SSR requests.
 * @param {Object} params - Parameters for building headers
 * @param {string} params.language - Language ID
 * @param {string} params.supplierToken - Supplier token
 * @param {string} [params.userId] - Logged-in user ID; when omitted, a Mealz authless id is fetched and sent
 * @param {string} params.sessionId - Session ID
 * @param {Object} params.extraHeaders - Extra headers
 * @returns {Promise<Object>} Headers object
 */
export async function buildSsrHeaders({
  language,
  supplierToken,
  userId,
  sessionId,
  cookiesConsent,
  extraHeaders = {},
  baseUrl,
}) {
  const headers = {
    "language-id": language,
    "Supplier-token": supplierToken,
    "session-id": sessionId,
    ...extraHeaders,
  };
  if (!cookiesConsent) {
    headers["cookies-consent"] = "false";
  }
  if (userId) {
    clearStoredAuthlessId();
    headers["authorization"] = "user_id " + userId;
  } else {
    headers["authless-id"] = await getOrCreateAuthlessId(supplierToken, baseUrl);
  }
  return headers;
}

/**
 * Fetch HTML from SSR API.
 * @param {string} endpoint - Endpoint to fetch
 * @param {Object} headers - Headers for the request
 * @param {Object} params - Parameters for the request
 * @returns {Promise<string>} HTML string
 */
export async function fetchSsrHtml(endpoint, headers, params = {}, baseUrl) {
  const resolvedBaseUrl = baseUrl ?? env?.ssr?.baseUrl;

  if (!resolvedBaseUrl) {
    throw new Error("[Mealz] baseUrl not configured");
  }

  const search = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== "") search.append(k, String(v));
  });
  const url = `${resolvedBaseUrl}${endpoint}?${search.toString()}`;

  return fetch(url, { method: "GET", headers }).then(async (res) => {
    if (!res.ok) {
      throw new Error(`[Mealz] ${url} fetch failed: ${res.status} ${res.statusText}`);
    }

    const html = await res.text();
    await loadModuleScriptsFromHtml(html);
    return html;
  });
}

/************************************** SSR STYLES FETCH UTILS **************************************/

/**
 * Insert <link rel="stylesheet"> tags into document head for each href not already present.
 * @param {string[]} links - Absolute stylesheet URLs from the styles API
 */
function applySsrStyleLinksToHead(links) {
  const head = document.head;
  if (!head) return;

  const existingHrefs = new Set(
    [...head.querySelectorAll('link[rel="stylesheet"]')].map((el) => el.href)
  );

  for (const href of links) {
    if (!href || existingHrefs.has(href)) continue;

    const linkEl = document.createElement("link");
    linkEl.rel = "stylesheet";
    linkEl.href = href;
    head.appendChild(linkEl);
    existingHrefs.add(href);
  }
}

/**
 * Fetch styles from SSR API (/styles/... JSON with links + optional html) and append link tags to the head.
 * @param {string} endpoint - Path including version, e.g. /v3/styles/catalog/catalog-home
 */
export async function fetchSsrStyles(endpoint, baseUrl) {
  const resolvedBaseUrl = baseUrl ?? env?.ssr?.baseUrl;

  if (!resolvedBaseUrl) {
    throw new Error("[Mealz] baseUrl not configured");
  }

  const url = `${resolvedBaseUrl}${endpoint}`;
  const res = await fetch(url, { method: "GET" });
  if (!res.ok) {
    throw new Error(`[Mealz] ${url} fetch failed: ${res.status} ${res.statusText}`);
  }

  const payload = await res.json();
  const links = payload?.links;
  if (!Array.isArray(links)) {
    throw new TypeError('[Mealz] styles response missing links array');
  }

  applySsrStyleLinksToHead(links);
}

/**
 *************************************** DOM UTILS **************************************/

/**
 * Wait for an element to be present in the DOM.
 * @param {string} selector - Selector to wait for
 * @param {Object} options - Options for the wait
 * @param {number} options.maxAttempts - Maximum number of attempts
 * @param {number} options.intervalMs - Interval between attempts
 * @returns {Promise<Element>} Element
 */
export function waitForElement(
  selector,
  { maxAttempts = 20, intervalMs = 250 } = {}
) {
  return new Promise((resolve) => {
    let attempt = 0;
    const tryFind = () => {
      const el = document.querySelector(selector);
      if (el) return resolve(el);
      attempt += 1;
      if (attempt >= maxAttempts) return resolve(null);
      setTimeout(tryFind, intervalMs);
    };
    tryFind();
  });
}

/**
 * Execute a function when the DOM is ready.
 * @param {Function} fn Function to execute when the DOM is ready
 */
export function onReady(fn) {
  if (
    document.readyState === "complete" ||
    document.readyState === "interactive"
  ) {
    setTimeout(fn, 0);
  } else {
    document.addEventListener("DOMContentLoaded", () => fn());
  }
}

/**
 * TODO: REMOVE WHEN MEALZ V10 IS REMOVED
 */
export function configureMealzV10IfPresent({
  token,
  language,
  hookCallback,
  storeId,
  userId,
  pushProductsToBasket
}) {
  try {
    if (typeof window !== "undefined" && window.mealzV10) {
      if (token) window.mealzV10.supplier.setupWithToken(token);
      if (userId) window.mealzV10.user.loadWithExternalId(userId);
      if (language) window.mealzV10.user.setLanguage(language);
      if (hookCallback) window.mealzV10.hook.setHookCallback(hookCallback);
      if (storeId) window.mealzV10.pos.load(storeId);
      if (pushProductsToBasket) {
        window.mealzV10.basketSync.definePushProductsToCart(pushProductsToBasket);
      }
      window.mealzV10.basket.initialize();
    }
  } catch (_) {
    console.debug("[Mealz] configureMealzV10IfPresent failed");
  }
}